import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Modal } from 'antd';

import EventFormElement from '../events/EventFormElement';
import { getElementInitialValue, getMissingRequiredElements, isElementValueEmpty } from '../../utils/elements';
import { validateElementValue } from '../../utils/elementValidation';
import { actionLabel, inputAsElement, pressRefusal } from '../../utils/cardActions';

/**
 * The small form a card action asks before it runs (KPR-551; owner ruling d54).
 * One field per input the manager defined, drawn by the same renderer a form
 * uses. Each value is judged here first (required, the type's own rule) and the
 * API judges again: its reason for refusing the press ("input_required",
 * "invalid_input", a lock…) shows in the dialog, in its words, and the person
 * can change a value and press again.
 *
 * `onPress(values)` makes the press and resolves when it is done (the dialog
 * then closes) or rejects with the refusal (the dialog stays).
 */
export default function CardActionInputsModal({ open, row, onCancel, onPress }) {
	const inputs = useMemo(() => (row?.inputs || []).map(inputAsElement), [row]);
	const [values, setValues] = useState({});
	const [errors, setErrors] = useState({});
	const [refusal, setRefusal] = useState('');
	const [busy, setBusy] = useState(false);
	const body = useRef(null);

	// A fresh form each time it opens.
	useEffect(() => {
		if (!open) return;
		const seeded = {};
		inputs.forEach(el => { seeded[el.name] = getElementInitialValue(el, { isNewRecord: true }); });
		setValues(seeded);
		setErrors({});
		setRefusal('');
		setBusy(false);
	}, [open, inputs]);

	const submit = async () => {
		if (busy) return;
		const next = {};
		getMissingRequiredElements(inputs, values).forEach(el => { next[el.name] = `${el.label.singular} is required.`; });
		inputs.forEach(el => {
			if (next[el.name]) return;
			const message = validateElementValue(el, values[el.name]);
			if (message) next[el.name] = message;
		});
		setErrors(next);
		setRefusal('');
		if (Object.keys(next).length) return;
		const given = {};
		inputs.forEach(el => { if (!isElementValueEmpty(el, values[el.name])) given[el.name] = values[el.name]; });
		setBusy(true);
		try {
			await onPress(given);
		} catch (error) {
			setRefusal(pressRefusal(error));
		}
		setBusy(false);
	};

	// Focus opens on the first field, not on the dialog's button.
	const focusFirstField = isOpen => {
		if (!isOpen) return;
		const field = body.current?.querySelector('input:not([type="hidden"]):not([disabled]), textarea:not([disabled])');
		if (field) field.focus();
	};
	// Enter in a one-line field presses the action, as it would in any form.
	// A text area keeps Enter for its line break; busy is guarded in submit.
	const onFieldsKeyDown = event => {
		if (event.key !== 'Enter' || event.defaultPrevented || event.nativeEvent?.isComposing) return;
		const target = event.target;
		if (!target || target.tagName !== 'INPUT') return;
		if (target.getAttribute('role') === 'combobox' && target.getAttribute('aria-expanded') === 'true') return;
		event.preventDefault();
		submit();
	};

	if (!row) return null;
	const label = actionLabel(row);
	return (
		<Modal
			open={open}
			title={label}
			okText={label}
			cancelText="Cancel"
			onOk={submit}
			onCancel={busy ? undefined : onCancel}
			confirmLoading={busy}
			maskClosable={!busy}
			destroyOnClose
			afterOpenChange={focusFirstField}
			width={480}
			className="card-action-inputs"
		>
			{row.button?.confirm ? <p className="card-action-inputs__confirm">{row.button.confirm}</p> : null}
			<div className="card-action-inputs__fields" ref={body} onKeyDown={onFieldsKeyDown}>
				{inputs.map(el => (
					<div key={el.name} className="item-form-field card-action-inputs__field">
						<EventFormElement
							event={{ _id: null, elements: values }}
							element={el}
							value={values[el.name]}
							onChange={(name, value) => {
								setValues(prev => ({ ...prev, [name]: value }));
								setErrors(prev => (prev[name] ? { ...prev, [name]: undefined } : prev));
							}}
							showIconBadge={false}
							errorMessage={errors[el.name] || null}
						/>
					</div>
				))}
			</div>
			{refusal ? <Alert type="error" showIcon message={refusal} role="alert" /> : null}
		</Modal>
	);
}
